/* @moldura padrao */
/**
 * page.tsx (Configurações) — o que a operação ajusta sem deploy.
 *
 * ─── A TELA É GENÉRICA, e essa é a decisão que a define ────────────────────
 * Ela não conhece parâmetro nenhum por nome. Lê `/configuracoes/parametros` e
 * desenha o que vier: o `grupo` de cada linha vira uma aba, o `tipo` escolhe o
 * controle, a `unidade` escolhe qual campo numérico. **Parâmetro novo nasce
 * numa migration e aparece aqui sozinho** — nenhuma lista escrita à mão neste
 * arquivo, pela mesma razão da tela de Perfis & Acessos.
 *
 * Antes de 28/08/2026 esta tela era só o Tiny, e a `tbl_config_parametros`
 * existia desde a migration 057 governando o app sem ter onde ser mexida: o
 * único caminho era `UPDATE` em produção, que é exatamente o que a tabela foi
 * criada para evitar.
 *
 * ─── DUAS NATUREZAS DE ABA ─────────────────────────────────────────────────
 * As abas de PARÂMETRO nascem dos dados e mudam sozinhas. A de INTEGRAÇÕES é
 * fixa, tem dono próprio (`aba-integracoes.tsx`) e não tem nada a ver com
 * parâmetro — só divide a tela. Por isso ela é uma constante à parte, e não um
 * grupo fingindo ser um.
 *
 * ─── SALVAR É POR CAMPO ────────────────────────────────────────────────────
 * Não há botão "Salvar tudo". Cada parâmetro grava sozinho ao perder o foco (ou
 * ao virar o interruptor), porque eles são independentes entre si: um botão só
 * faria parecer que mexer em dois é uma decisão, e o erro de um derrubaria o
 * outro. O servidor devolve a linha como ficou — e é ela que a tela passa a
 * mostrar, já que `validar()` normaliza o valor.
 */
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { api, ApiError } from '@/lib/api';
import { AbaIntegracoes } from './aba-integracoes';
import { CustomCampoParametro, type Parametro } from './CustomCampoParametro';
import { CustomMolduraPagina } from '@/components/CustomMolduraPagina';

/** Rótulo e ícone de cada grupo. Grupo novo cai no padrão sem quebrar nada. */
const GRUPOS: Record<string, { nome: string; icone: string }> = {
  geral: { nome: 'Geral', icone: 'bi-sliders' },
  acesso: { nome: 'Acesso', icone: 'bi-shield-lock' },
  operacao: { nome: 'Operação', icone: 'bi-clipboard-check' },
};
const grupoDe = (g: string) => GRUPOS[g] ?? { nome: g, icone: 'bi-gear' };

/** A aba das integrações não é um grupo de parâmetro — é outra coisa. */
const ABA_INTEGRACOES = '__integracoes';

export function ConfiguracoesPage() {
  const [itens, setItens] = useState<Parametro[]>([]);
  const [rascunho, setRascunho] = useState<Record<string, string>>({});
  const [carregando, setCarregando] = useState(true);
  const [aba, setAba] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const lista = await api.get<Parametro[]>('/configuracoes/parametros');
        setItens(lista);
        setRascunho(Object.fromEntries(lista.map((p) => [p.chave, p.valor])));
      } catch {
        /* silencioso: sem parâmetros a tela ainda serve às integrações */
      } finally {
        setCarregando(false);
      }
    })();
  }, []);

  /** Os grupos que EXISTEM, na ordem em que o servidor os devolveu. */
  const grupos = useMemo(() => {
    const vistos: string[] = [];
    for (const p of itens) if (!vistos.includes(p.grupo)) vistos.push(p.grupo);
    return vistos;
  }, [itens]);

  // A aba inicial só se decide depois de saber quais existem. Integrações é o
  // fallback porque ela existe sempre, mesmo sem nenhum parâmetro cadastrado.
  useEffect(() => {
    if (aba === null && !carregando) setAba(grupos[0] ?? ABA_INTEGRACOES);
  }, [aba, carregando, grupos]);

  async function gravar(p: Parametro, valor: string) {
    if (valor === p.valor) return; // nada mudou: não incomoda o servidor
    try {
      const linha = await api.put<Parametro>(
        `/configuracoes/parametros/${p.chave}`,
        { valor },
      );
      setItens((atual) => atual.map((x) => (x.chave === p.chave ? linha : x)));
      setRascunho((atual) => ({ ...atual, [p.chave]: linha.valor }));
      toast.success(`${p.nome} atualizado.`);
    } catch (e) {
      // Devolve o campo ao valor do servidor: deixar na tela um número que não
      // foi gravado é a forma mais rápida de alguém achar que gravou.
      setRascunho((atual) => ({ ...atual, [p.chave]: p.valor }));
      toast.error(e instanceof ApiError ? e.message : 'Não foi possível salvar.');
    }
  }

  const daAba = itens.filter((p) => p.grupo === aba);

  return (
    <CustomMolduraPagina
      titulo="Configurações"
      descricao="O que a operação ajusta sem precisar de uma nova versão do sistema."
    >

      <ul className="nav nav-tabs adminuiux-tabs mb-3 mb-lg-4">
        {grupos.map((g) => (
          <li className="nav-item" key={g}>
            <button
              type="button"
              className={`nav-link ${aba === g ? 'active' : ''}`}
              onClick={() => setAba(g)}
            >
              <i className={`bi ${grupoDe(g).icone} me-2`}></i>
              {grupoDe(g).nome}
            </button>
          </li>
        ))}
        <li className="nav-item">
          <button
            type="button"
            className={`nav-link ${aba === ABA_INTEGRACOES ? 'active' : ''}`}
            onClick={() => setAba(ABA_INTEGRACOES)}
          >
            <i className="bi bi-plug me-2"></i>Integrações
          </button>
        </li>
      </ul>

      {carregando && (
        <div className="d-flex align-items-center gap-2 text-secondary py-3">
          <span className="spinner-border spinner-border-sm" /> Carregando…
        </div>
      )}

      {aba === ABA_INTEGRACOES && <AbaIntegracoes />}

      {aba !== null && aba !== ABA_INTEGRACOES && (
        <div className="card adminuiux-card">
          <div className="card-header bg-transparent">
            <h6 className="mb-0">
              <i className={`bi ${grupoDe(aba).icone} me-2`}></i>
              {grupoDe(aba).nome}
            </h6>
          </div>
          <div className="card-body">
            {daAba.map((p, i) => (
              <div
                key={p.chave}
                className={`row gx-3 align-items-center py-3 ${i > 0 ? 'border-top' : ''}`}
              >
                <div className="col-12 col-lg-7">
                  <p className="mb-0 fw-medium">{p.nome}</p>
                  {p.descricao && (
                    <p className="text-secondary small mb-0">{p.descricao}</p>
                  )}
                  {/* Só-leitura não fica escondido: quem administra precisa
                      saber que o parâmetro existe e quanto vale, mesmo sem
                      poder mexer. Sem o aviso, o campo cinza parece defeito. */}
                  {!p.editavel && (
                    <p className="text-secondary small mb-0">
                      <i className="bi bi-lock me-1"></i>
                      Só o código altera este.
                    </p>
                  )}
                </div>
                <div className="col-12 col-lg-5 mt-2 mt-lg-0">
                  <CustomCampoParametro
                    parametro={p}
                    valor={rascunho[p.chave] ?? p.valor}
                    aoMudar={(novo) => {
                      setRascunho((atual) => ({ ...atual, [p.chave]: novo }));
                      // Interruptor grava na hora: não há "sair do campo" num
                      // clique, e esperar seria pedir um segundo gesto que
                      // ninguém faria.
                      if (p.tipo === 'booleano') gravar(p, novo);
                    }}
                  />
                </div>
              </div>
            ))}
            {daAba.length === 0 && !carregando && (
              <p className="text-secondary small mb-0">
                Nenhum parâmetro neste grupo.
              </p>
            )}
          </div>
          {daAba.some((p) => p.editavel && p.tipo !== 'booleano') && (
            <div className="card-footer bg-transparent">
              <button
                type="button"
                className="btn btn-sm btn-theme"
                onClick={() =>
                  daAba.forEach((p) => gravar(p, rascunho[p.chave] ?? p.valor))
                }
              >
                <i className="bi bi-check-lg me-1"></i>Salvar
              </button>
            </div>
          )}
        </div>
      )}
    </CustomMolduraPagina>
  );
}
